import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';

type DragMode = 'none' | 'pending' | 'active';

@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bottom-sheet.component.html',
  styleUrls: ['./bottom-sheet.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomSheetComponent implements OnDestroy {
  @ViewChild('sheet', { static: false }) private sheetRef?: ElementRef<HTMLElement>;
  @ViewChild('backdrop', { static: false }) private backdropRef?: ElementRef<HTMLElement>;
  @ViewChild('sheetBody', { static: false }) private sheetBodyRef?: ElementRef<HTMLElement>;

  @Input() ariaLabel = '';
  @Input() dark = false;
  @Input() darkTheme: { [key: string]: string } = {};
  @Input() height = 'auto';
  @Input() edgeToEdge = false;

  @Output() close = new EventEmitter<void>();

  public shouldRender = false;
  public isVisible = false;
  public isDragging = false;

  private _open = false;
  private closeTimer?: ReturnType<typeof setTimeout>;
  private portalHost: HTMLElement | null = null;
  private dragMode: DragMode = 'none';
  private dragStartY = 0;
  private dragCurrentY = 0;
  private dragStartTime = 0;
  private dragPointerId: number | null = null;
  private bodyScrollTopAtStart = 0;
  private removeMoveListener?: () => void;
  private removeUpListener?: () => void;
  private removeCancelListener?: () => void;
  private resizeObserver?: ResizeObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    @Inject(DOCUMENT) private doc: Document,
  ) {}

  @Input()
  set open(value: boolean) {
    if (value === this._open) {
      return;
    }
    this._open = value;
    if (value) {
      this.showSheet();
    } else {
      this.hideSheet('input');
    }
  }

  get open(): boolean {
    return this._open;
  }

  get isAutoHeight(): boolean {
    const value = (this.height ?? 'auto').trim().toLowerCase();
    return value === 'auto' || value === 'fit-content';
  }

  onBackdropClick(): void {
    if (this.isDragging || this.dragMode !== 'none') {
      return;
    }
    this.requestClose();
  }

  onPointerDown(event: PointerEvent): void {
    if (!this.shouldRender || !this.isVisible || event.button !== 0) {
      return;
    }

    const sheet = this.sheetRef?.nativeElement;
    if (!sheet) {
      return;
    }

    const target = event.target as Element;
    if (this.shouldIgnoreDragTarget(target)) {
      return;
    }

    this.clearCloseTimer();
    this.cleanupDragListeners();
    this.dragMode = 'pending';
    this.dragStartY = event.clientY;
    this.dragCurrentY = event.clientY;
    this.dragStartTime = Date.now();
    this.dragPointerId = event.pointerId;
    this.bodyScrollTopAtStart = this.sheetBodyRef?.nativeElement?.scrollTop ?? 0;

    const inTopZone = this.isInTopDragZone(target);
    if (inTopZone || this.bodyScrollTopAtStart <= 0) {
      if (inTopZone) {
        this.activateDrag(event, sheet);
      } else {
        this.attachDragListeners();
      }
    }
  }

  requestClose(): void {
    if (!this.shouldRender) {
      return;
    }
    this.hideSheet('internal');
    this.close.emit();
  }

  private showSheet(): void {
    this.clearCloseTimer();
    this.shouldRender = true;
    this.cdr.detectChanges();
    this.attachToBody();
    requestAnimationFrame(() => {
      this.isVisible = true;
      this.cdr.detectChanges();
      this.animateSheetIn();
      if (this.isAutoHeight) {
        requestAnimationFrame(() => {
          this.updateAutoHeight();
          this.observeAutoHeight();
        });
      }
    });
  }

  private hideSheet(source: 'input' | 'internal'): void {
    if (!this.shouldRender) {
      return;
    }
    if (source === 'internal') {
      this._open = false;
    }
    this.isVisible = false;
    this.cdr.detectChanges();
    this.disconnectAutoHeightObserver();
    this.animateSheetOut();
  }

  private animateSheetIn(): void {
    const sheet = this.sheetRef?.nativeElement;
    if (!sheet) {
      return;
    }
    sheet.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
    sheet.style.transform = 'translate3d(0, 0, 0)';
    this.setBackdropOpacity(1, true);
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.dragMode === 'none' || event.pointerId !== this.dragPointerId) {
      return;
    }

    const sheet = this.sheetRef?.nativeElement;
    if (!sheet) {
      return;
    }

    if (this.dragMode === 'pending') {
      const deltaY = event.clientY - this.dragStartY;
      const bodyScrollTop = this.sheetBodyRef?.nativeElement?.scrollTop ?? 0;

      if (deltaY > 6 && bodyScrollTop <= 0) {
        this.activateDrag(event, sheet);
      } else if (deltaY < -6) {
        this.resetDragState();
        return;
      } else {
        return;
      }
    }

    if (this.dragMode !== 'active') {
      return;
    }

    event.preventDefault();
    this.dragCurrentY = event.clientY;
    const deltaY = Math.max(0, this.dragCurrentY - this.dragStartY);
    this.applyDragTransform(sheet, deltaY);

    const body = this.sheetBodyRef?.nativeElement;
    if (body) {
      body.style.overflow = 'hidden';
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.dragMode === 'none' || event.pointerId !== this.dragPointerId) {
      return;
    }

    const sheet = this.sheetRef?.nativeElement;
    if (!sheet) {
      this.resetDragState();
      return;
    }

    if (this.dragMode === 'pending') {
      this.resetDragState();
      return;
    }

    this.dragCurrentY = event.clientY;
    const elapsedMs = Math.max(1, Date.now() - this.dragStartTime);
    const deltaY = Math.max(0, this.dragCurrentY - this.dragStartY);
    const velocity = deltaY / elapsedMs;
    const threshold = Math.max(80, sheet.offsetHeight * 0.22);
    const fastSwipe = velocity > 0.65 && deltaY > 20;

    this.resetDragState();

    if (deltaY >= threshold || fastSwipe) {
      this.completeDragClose(sheet);
      return;
    }

    this.snapSheetBack(sheet);
  }

  private activateDrag(event: PointerEvent, sheet: HTMLElement): void {
    this.dragMode = 'active';
    this.isDragging = true;
    sheet.style.transition = 'none';
    this.setBackdropOpacity(this.getBackdropOpacityForDelta(0), false);
    this.cdr.markForCheck();

    if (typeof (event.target as Element)?.setPointerCapture === 'function') {
      try {
        sheet.setPointerCapture(event.pointerId);
      } catch {
        // ignore capture failures on unsupported targets
      }
    }

    if (!this.removeMoveListener) {
      this.attachDragListeners();
    }
  }

  private attachDragListeners(): void {
    this.removeMoveListener = this.renderer.listen(this.doc, 'pointermove', (moveEvent: PointerEvent) => {
      this.onPointerMove(moveEvent);
    });
    this.removeUpListener = this.renderer.listen(this.doc, 'pointerup', (upEvent: PointerEvent) => {
      this.onPointerUp(upEvent);
    });
    this.removeCancelListener = this.renderer.listen(this.doc, 'pointercancel', (cancelEvent: PointerEvent) => {
      this.onPointerUp(cancelEvent);
    });
  }

  private applyDragTransform(sheet: HTMLElement, deltaY: number): void {
    sheet.style.transform = `translate3d(0, ${deltaY}px, 0)`;
    this.setBackdropOpacity(this.getBackdropOpacityForDelta(deltaY, sheet.offsetHeight), false);
  }

  private getBackdropOpacityForDelta(deltaY: number, sheetHeight?: number): number {
    const height = sheetHeight ?? this.sheetRef?.nativeElement?.offsetHeight ?? 1;
    const progress = Math.min(1, deltaY / Math.max(height, 1));
    return Math.max(0, 1 - progress);
  }

  private snapSheetBack(sheet: HTMLElement): void {
    sheet.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
    sheet.style.transform = 'translate3d(0, 0, 0)';
    this.setBackdropOpacity(1, true);
    this.restoreBodyScroll();
  }

  private completeDragClose(sheet: HTMLElement): void {
    const height = sheet.offsetHeight;
    sheet.style.transition = 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)';
    sheet.style.transform = `translate3d(0, ${height}px, 0)`;
    this.setBackdropOpacity(0, true);
    this.restoreBodyScroll();

    this._open = false;
    this.isVisible = false;
    this.cdr.detectChanges();

    this.closeTimer = setTimeout(() => {
      this.close.emit();
      this.shouldRender = false;
      this.cdr.detectChanges();
      this.resetSheetTransform();
      this.resetBackdropStyles();
      this.detachFromBody();
      this.clearCloseTimer();
    }, 320);
  }

  private animateSheetOut(): void {
    const sheet = this.sheetRef?.nativeElement;
    if (sheet) {
      const height = sheet.offsetHeight;
      sheet.style.transition = 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)';
      sheet.style.transform = `translate3d(0, ${height}px, 0)`;
    }
    this.setBackdropOpacity(0, true);

    this.closeTimer = setTimeout(() => {
      this.shouldRender = false;
      this.cdr.detectChanges();
      this.resetSheetTransform();
      this.resetBackdropStyles();
      this.detachFromBody();
      this.clearCloseTimer();
    }, 350);
  }

  private setBackdropOpacity(opacity: number, animate: boolean): void {
    const backdrop = this.backdropRef?.nativeElement;
    if (!backdrop) {
      return;
    }

    backdrop.style.transition = animate ? 'opacity 0.35s cubic-bezier(0.32, 0.72, 0, 1)' : 'none';
    backdrop.style.opacity = String(Math.max(0, Math.min(1, opacity)));
  }

  private resetBackdropStyles(): void {
    const backdrop = this.backdropRef?.nativeElement;
    if (!backdrop) {
      return;
    }
    backdrop.style.transition = '';
    backdrop.style.opacity = '';
  }

  private resetSheetTransform(): void {
    const sheet = this.sheetRef?.nativeElement;
    if (!sheet) {
      return;
    }
    sheet.style.transition = '';
    sheet.style.transform = '';
    sheet.style.height = '';
    sheet.style.maxHeight = '';
    sheet.classList.remove('bottom-sheet--capped');
    this.restoreBodyScroll();
  }

  private updateAutoHeight(): void {
    const sheet = this.sheetRef?.nativeElement;
    const body = this.sheetBodyRef?.nativeElement;
    if (!sheet || !this.isAutoHeight) {
      return;
    }

    sheet.style.height = 'auto';
    sheet.style.maxHeight = 'none';
    sheet.classList.remove('bottom-sheet--capped');

    const contentHeight = sheet.scrollHeight;
    const maxHeight = Math.floor(window.innerHeight * 0.9);

    if (contentHeight > maxHeight) {
      sheet.style.height = `${maxHeight}px`;
      sheet.style.maxHeight = `${maxHeight}px`;
      sheet.classList.add('bottom-sheet--capped');
      if (body) {
        body.style.overflowY = 'auto';
      }
      return;
    }

    sheet.style.height = `${contentHeight}px`;
    if (body) {
      body.style.overflowY = 'visible';
    }
  }

  private observeAutoHeight(): void {
    this.disconnectAutoHeightObserver();
    if (!this.isAutoHeight) {
      return;
    }

    const sheet = this.sheetRef?.nativeElement;
    if (!sheet || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => this.updateAutoHeight());
    this.resizeObserver.observe(sheet);
  }

  private disconnectAutoHeightObserver(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  private resetDragState(): void {
    this.dragMode = 'none';
    this.isDragging = false;
    this.dragPointerId = null;
    this.cleanupDragListeners();
    this.restoreBodyScroll();
    this.cdr.markForCheck();
  }

  private restoreBodyScroll(): void {
    const body = this.sheetBodyRef?.nativeElement;
    if (body) {
      body.style.overflow = '';
    }
  }

  private isInTopDragZone(target: Element): boolean {
    return !!target.closest('.bottom-sheet-handle, [sheetHeader]');
  }

  private shouldIgnoreDragTarget(target: Element): boolean {
    return !!target.closest('button, a, input, select, textarea, label, [role="button"]');
  }

  private attachToBody(): void {
    if (this.portalHost) {
      return;
    }
    const host = this.el.nativeElement;
    this.portalHost = host;
    this.renderer.appendChild(this.doc.body, host);
  }

  private detachFromBody(): void {
    if (!this.portalHost) {
      return;
    }
    try {
      this.renderer.removeChild(this.doc.body, this.portalHost);
    } catch {
      // already detached
    }
    this.portalHost = null;
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
  }

  private cleanupDragListeners(): void {
    if (this.removeMoveListener) {
      this.removeMoveListener();
      this.removeMoveListener = undefined;
    }
    if (this.removeUpListener) {
      this.removeUpListener();
      this.removeUpListener = undefined;
    }
    if (this.removeCancelListener) {
      this.removeCancelListener();
      this.removeCancelListener = undefined;
    }
  }

  ngOnDestroy(): void {
    this.clearCloseTimer();
    this.cleanupDragListeners();
    this.disconnectAutoHeightObserver();
    this.detachFromBody();
  }
}
